/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './DarkModeToggle';

const DarkModeToggleManager: ComponentManagerFn<'DarkModeToggle'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'DarkModeToggle'>(platform, 'DarkModeToggle'),
    type: 'DarkModeToggle',
    name: 'Dark Mode Toggle',
    defaultValue,
    props: {},
    category: 'Page Header',
    icon: { iconName: 'moon' },
    description: `**Overview**<br/>
The Dark Mode Toggle is a utility component that renders a switch icon (typically a moon/sun symbol), allowing users to toggle the application's visual theme between Light Mode and Dark Mode in real-time.
**Note:** The standard application header normally includes this control by default. This component is specifically provided for scenarios where you have hidden the default header and are building a **Custom Header**, but still wish to offer theme-switching functionality to your users.

**1. Configuration**<br/>
* **Zero Configuration:** This component accepts no manual properties (props: {}). Simply adding it to your layout will render the functional toggle button. 
* **Automatic Integration:** It automatically hooks into the platform's global theming engine to handle the state changes across all pages and components without requiring any custom code or logic.

**2. Best Use Cases**<br/>
* **Custom Headers:** Reinstating the theme toggle when designing a fully custom-built navigation bar to ensure users don't lose access to personalization settings.
* **Accessibility Panels:** Placing the toggle in a "Settings," "User Profile," or "Appearance" side panel rather than the main top header to keep the primary interface clean.
* **User Preference & Comfort:** Allowing users to switch to a darker interface for low-light environments to reduce eye strain and improve readability during night-time operation.
* **Branding:** Testing how custom dashboards and charts appear in both light and dark variations during the design phase.`,
  };
};

export default DarkModeToggleManager;
